import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { buildIntegrationPrompt } from '@admin-platform/shared/integration-content';

const AGENT_PROMPT = buildIntegrationPrompt('apiKeys');

export function ApiKeysIntegrationGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Configure integration API keys — Revvly Docs';
  }, []);
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link><span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Integration API keys</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>Configure an integration <em>API key</em>.</h1>
        <p className="dp-oneliner">
          Issue the least-privileged credentials your worker needs, store them once, and rotate
          without interrupting ingestion or payment callbacks.
        </p>
      </header>
      <section className="dp-section">
        <h2>Give this prompt to an integration agent</h2>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>
      <section className="dp-section">
        <h2>Choose the correct authentication model</h2>
        <div className="dp-fields-table">
          <div className="dp-field-row"><div className="dp-field-k">Public GraphQL<br /><span className="api-inline">POST https://api.revvly.ai/api/v1/graphql</span></div><div className="dp-field-v">Bearer key. Queries need READ. Mutations need WRITE, except createEndUserToken and upsertSupportUsers (SUPPORT_TOKEN_MINT), submitForm (FORM_SUBMIT) and the knowledge mutations (KNOWLEDGE_WRITE), where WRITE is neither needed nor sufficient.</div></div>
          <div className="dp-field-row"><div className="dp-field-k">Usage REST<br /><span className="api-inline">https://api.revvly.ai/api/v1/usage*</span></div><div className="dp-field-v">Bearer key plus Idempotency-Key for mutations; no request HMAC. READ and WRITE are not checked here; the key, its endpoint scope and its subscription and meter scope are.</div></div>
          <div className="dp-field-row"><div className="dp-field-k">Event ingestion<br /><span className="api-inline">POST https://api.revvly.ai/api/v1/events</span></div><div className="dp-field-v">Bearer key and HMAC-SHA256 of the raw body with the signing secret.</div></div>
          <div className="dp-field-row"><div className="dp-field-k">Invoice payment webhook<br /><span className="api-inline">POST https://api.revvly.ai/api/v1/webhooks/invoice-payment</span></div><div className="dp-field-v">Bearer key, raw-body HMAC, and a stable Idempotency-Key.</div></div>
        </div>
      </section>
      <section className="dp-section">
        <h2>Scope and rotate safely</h2>
        <p className="dp-plain">
          Restrict a key to the subscriptions, meters, and endpoints its worker actually needs.
          During rotation, deploy the new token and signing secret to the worker before removing
          the old values. Keep credentials in a server-side secret manager and redact them from
          observability output.
        </p>
        <p className="dp-plain">
          Every key is limited per key to 10 requests per second and 100 per minute unless the
          issuer sets other values. A refused request returns 429 with a{' '}
          <span className="api-inline">Retry-After</span> header. Capabilities, limits and the 429
          body are listed in the{' '}
          <Link to="/docs/reference/api-keys">API key reference</Link>.
        </p>
      </section>
      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li><Link to="/docs/api/events" style={{ color: 'inherit', textDecoration: 'none' }}>Event Ingestion API ›</Link></li>
          <li><Link to="/docs/api/graphql-public" style={{ color: 'inherit', textDecoration: 'none' }}>Public GraphQL API ›</Link></li>
          <li><Link to="/docs/api/invoice-payments" style={{ color: 'inherit', textDecoration: 'none' }}>Invoice payment webhook ›</Link></li>
        </ul>
      </section>
    </div>
  );
}
